import { api } from '../lib/api'

export type TreasuryChain = {
  name: string
  rpc: string
  rpcConfigured?: string
  rpcEffective?: string
  error?: string | null
  ss58: number
  healthy: boolean
  latestBlock: string | number | null
  latestBlockHex?: string | null
  peers: number | null
  isSyncing?: boolean | null
}

export type TreasuryHouseAccount = {
  key: string
  label: string
  ss58: string | null
  role: 'hot' | 'cold' | 'watch' | 'unconfigured'
  balancePixe: string | null
  balancePlanck?: string | null
  availablePixe?: string | null
  heldPixe?: string | null
  totalPixe?: string | null
  availablePlanck?: number | null
  heldPlanck?: number | null
  totalPlanck?: number | null
  balanceSource?: 'ledger' | 'chain'
  balanceError?: string | null
  chainBalance?: {
    freePlanck: number
    freePixe: string
  } | null
  hasKey?: boolean
  canSend?: boolean
  sendDisabledReasons?: string[]
  status: string
}

export type TreasurySummary = {
  chain: TreasuryChain
  houseAccounts: TreasuryHouseAccount[]
  settings?: {
    sendsEnabled?: boolean
    vaultMovesEnabled?: boolean
    perTxLimitPlanck?: number
    perDayLimitPlanck?: number
    dailyRemainingPlanck?: number
    minTreasuryBalancePlanck?: number
    confirmationsRequired?: number
    confirmPhrase?: string
    circuitTripped?: boolean
    dailyRemainingPlanck?: number | null
  }
  reconciliation?: {
    lastRunAt?: string
    mismatches?: number
    status?: string
    basis?: string
  }
  kpis: {
    outgoing24h: string
    outgoing7d: string
    pendingOps: string
    walletsProvisionedPct: string
    walletsProvisionedCount?: number
    walletsEligibleCount?: number
  }
}

export type TreasuryActivityItem = {
  id: string | number
  ts: string
  type: string
  from: string
  to: string
  amountPixe: string
  amountPlanck?: number
  status: string
  memo?: string
  txHash: string | null
  blockNumber?: number | null
  submitErrorCode?: string | null
}

export type TreasuryCreateWalletResponse = {
  houseKey: string
  ss58: string
  hasKey: boolean
  revealed: boolean
  mnemonic?: string | null
}

export async function getTreasurySummary(): Promise<TreasurySummary> {
  const { data } = await api.get<TreasurySummary>('/api/account/settings/treasury/summary')
  return data
}

export async function getTreasuryActivity(limit = 50): Promise<TreasuryActivityItem[]> {
  const { data } = await api.get<{ results: TreasuryActivityItem[] }>(`/api/account/settings/treasury/activity?limit=${limit}`)
  return data?.results || []
}

export async function refreshTreasuryTxs(limit = 50): Promise<{ checked: number; advanced: number }> {
  const { data } = await api.post<{ checked: number; advanced: number }>(
    '/api/account/settings/treasury/txs/refresh',
    { limit }
  )
  return data
}

export async function refreshTreasuryTx(txId: number) {
  const { data } = await api.post<{
    id: number
    txHash: string | null
    status: string
    blockNumber?: number | null
    reason?: string | null
    source?: string | null
    submitErrorCode?: string | null
  }>(
    `/api/account/settings/treasury/tx/${txId}/refresh`,
    {}
  )
  return data
}

export async function provisionMissingWallets(): Promise<void> {
  await api.post('/api/account/settings/treasury/provision_missing_wallets')
}

export async function getHouseReceive(houseKey: string) {
  const { data } = await api.get<{ key: string; ss58: string | null; role: string }>(
    `/api/account/settings/treasury/receive/${houseKey}`
  )
  return data
}

export async function importHouseMnemonic(houseKey: string, mnemonic: string, opts?: { ackLargeBalanceRisk?: boolean }) {
  const { data } = await api.post<{ detail: string; ss58: string }>(
    `/api/account/settings/treasury/house_accounts/${houseKey}/import_key`,
    { mnemonic, ackLargeBalanceRisk: opts?.ackLargeBalanceRisk }
  )
  return data
}

export async function createHouseWallet(
  houseKey: string,
  payload: { revealOnce?: boolean; confirmPhrase?: string }
): Promise<TreasuryCreateWalletResponse> {
  const { data } = await api.post<TreasuryCreateWalletResponse>(
    `/api/account/settings/treasury/house_accounts/${houseKey}/create_key`,
    { ack: true, ...payload }
  )
  return data
}

export async function updateTreasuryPolicy(payload: {
  vaultMovesEnabled?: boolean
  sendsEnabled?: boolean
  perTxLimitPlanck?: number
  perDayLimitPlanck?: number
  minTreasuryBalancePlanck?: number
  confirmationsRequired?: number
  confirmPhrase?: string
}) {
  const { data } = await api.patch('/api/account/settings/treasury/policy', payload)
  return data
}

export async function runTreasuryReconciliation() {
  const { data } = await api.post<{ id: number; status: string; mismatches: number; lastRunAt: string }>(
    '/api/account/settings/treasury/reconciliation/run',
    {}
  )
  return data
}

export async function getTreasuryReconciliationStatus() {
  const { data } = await api.get<{ lastRunAt?: string; mismatches?: number; status?: string }>(
    '/api/account/settings/treasury/reconciliation/status'
  )
  return data
}

export async function sendFromTreasury(payload: {
  from_house_key?: string
  dest_ss58: string
  amount: string | number
  planck?: boolean
  memo?: string
}) {
  const { data } = await api.post<{ id: number; tx_hash: string; status: string }>(
    '/api/account/settings/treasury/send',
    payload
  )
  return data
}

export async function refillSellHot(payload: { amount: string | number; planck?: boolean; confirmPhrase: string; vaultMnemonic?: string }) {
  const { data } = await api.post<{ id?: number; tx_hash?: string; status?: string }>(
    '/api/account/settings/treasury/refill_sell_hot',
    payload
  )
  return data
}
